import React, { useState, useEffect, useMemo } from 'react';
import { useTheme } from '../../../theme';
import {
  View,
  Text,
  StyleSheet,
  Pressable,
  ScrollView,
  Dimensions,
  Platform,
  Linking,
} from 'react-native';
import Svg, { G, Circle, Line, Path } from 'react-native-svg';
import DLOTooltip from './DLOTooltip';
import LHSidebarItems from '../../../components/LHSidebarItems';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import FullStory from '../../../utils/fullStory';


interface DLOSidebarProps {
  activeScreen: string;
  onNavigate: (screen: string) => void;
  isVisible?: boolean;
}

const { height: screenHeight } = Dimensions.get('window');

// Constants for responsive design
const HEADER_HEIGHT = 64; // Match header height from Header component

// Helper function to get tooltip content based on sidebar item ID
const getTooltipContent = (itemId: string): { title: string; description: string } => {
  if (itemId === 'harbor') {
    return {
      title: 'Harbor - Perfect Truck',
      description: 'Unified entry point for delivery-related workflows including Perfect Truck and Delivery RPM. Access is role-based.'
    };
  }

  // Map DLO sidebar IDs to LHSidebarItems
  const idMapping: { [key: string]: string } = {
    'home': 'Home',
    'observations': 'Observations',
    'shift-handover': 'Shift Handover',
    'employee-update': 'Employee Update',
    'leadership-walk': 'Leadership Walk',
    'sparc-walk': 'SPARC Walk',
    'nho': 'NHO',
    'people-hub': 'People Hub',
    'racking-inspection': 'Racking Inspection',
    'quality-audit': 'Quality Audit',
    'site-evaluation': 'Site Evaluation',
    'analytics': 'Analytics',
    'action-plan': 'Action Plan',
    'stand-up': 'Stand Up',
    'labor-optimization': 'Labor Optimization', // Map to Labor Optimization in LHSidebarItems
    'perfect-truck': 'Perfect Truck',
    'harbor': 'Harbor',
    'ticketing-portal': 'Ticketing Portal - Contact Us',
  };

  const mappedLabel = idMapping[itemId];
  const sidebarItem = LHSidebarItems.find(item => item.label === mappedLabel);
  
  if (sidebarItem) {
    return {
      title: sidebarItem.label,
      description: sidebarItem.description || `Navigate to ${sidebarItem.label}`
    };
  }

  // Fallback for items not found
  if (itemId === 'action-plan') {
    return {
      title: 'Action Plan',
      description: 'Create, track, and manage action plans. Monitor progress, assign responsibilities, and ensure timely completion of action items to drive continuous improvement.'
    };
  }

  if (itemId === 'stand-up') {
    return {
      title: 'Stand Up',
      description: 'Conduct and manage daily stand-up meetings to align team activities and track progress.'
    };
  }

  if (itemId === 'perfect-truck') {
    return {
      title: 'Perfect Truck',
      description: 'Manage and certify perfect truck operations including equipment checks and photo documentation.'
    };
  }

  if (itemId === 'ticketing-portal') {
    return {
      title: 'Ticketing Portal - Contact Us',
      description: 'Create and track support tickets and contact requests in one place.'
    };
  } 
  
  if (itemId === 'harbor') {
    return {
      title: 'Harbor - Perfect Truck',
      description: 'Unified entry point for delivery-related workflows including Perfect Truck and Delivery RPM. Access is role-based.'
    };
  }

  // Fallback for items not found
  return {
    title: itemId.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase()),
    description: `Navigate to ${itemId.replace('-', ' ')}`
  };
};

// SVG Icons - Based on the patterns from SidebarNavigation
const SideBarMenuIcon: React.FC<{ color?: string }> = ({ color = '#3A3A3A' }) => (
  <Path
    d="M2.5 15H13.3333V13.3333H2.5V15ZM2.5 10.8333H10.8333V9.16667H2.5V10.8333ZM2.5 5V6.66667H13.3333V5H2.5ZM17.5 12.9917L14.5167 10L17.5 7.00833L16.325 5.83333L12.1583 10L16.325 14.1667L17.5 12.9917Z"
    fill={color}
  />
);

const CloseIcon: React.FC<{ color?: string }> = ({ color = '#3A3A3A' }) => (
  <Path
    d="M2.5 15H13.3333V13.3333H2.5V15ZM2.5 10.8333H10.8333V9.16667H2.5V10.8333ZM2.5 5V6.66667H13.3333V5H2.5ZM17.5 12.9917L14.5167 10L17.5 7.00833L16.325 5.83333L12.1583 10L16.325 14.1667L17.5 12.9917Z"
    fill={color}
  />
);

const HomeIcon: React.FC<{ color?: string }> = ({ color = '#3A3A3A' }) => (
  <Path
    d="M10 4.74167L14.1667 8.49167V15H12.5V10H7.50002V15H5.83335V8.49167L10 4.74167V4.74167ZM10 2.5L1.66669 10H4.16669V16.6667H9.16669V11.6667H10.8334V16.6667H15.8334V10H18.3334L10 2.5Z"
    fill={color}
  />
);

const VisibilityIcon: React.FC<{ color?: string }> = ({ color = '#3A3A3A' }) => (
  <Path
    d="M9.99998 5C13.1583 5 15.975 6.775 17.35 9.58333C15.975 12.3917 13.1583 14.1667 9.99998 14.1667C6.84165 14.1667 4.02498 12.3917 2.64998 9.58333C4.02498 6.775 6.84165 5 9.99998 5ZM9.99998 3.33333C5.83331 3.33333 2.27498 5.925 0.833313 9.58333C2.27498 13.2417 5.83331 15.8333 9.99998 15.8333C14.1666 15.8333 17.725 13.2417 19.1666 9.58333C17.725 5.925 14.1666 3.33333 9.99998 3.33333ZM9.99998 7.5C11.15 7.5 12.0833 8.43333 12.0833 9.58333C12.0833 10.7333 11.15 11.6667 9.99998 11.6667C8.84998 11.6667 7.91665 10.7333 7.91665 9.58333C7.91665 8.43333 8.84998 7.5 9.99998 7.5ZM9.99998 5.83333C7.93331 5.83333 6.24998 7.51667 6.24998 9.58333C6.24998 11.65 7.93331 13.3333 9.99998 13.3333C12.0666 13.3333 13.75 11.65 13.75 9.58333C13.75 7.51667 12.0666 5.83333 9.99998 5.83333Z"
    fill={color}
  />
);

const SwapIcon: React.FC<{ color?: string }> = ({ color = '#3A3A3A' }) => (
  <Path
    d="M5.825 9.16666L2.5 12.5L5.825 15.8333V13.3333H11.6667V11.6667H5.825V9.16666ZM17.5 7.5L14.175 4.16666V6.66666H8.33333V8.33333H14.175V10.8333L17.5 7.5Z"
    fill={color}
  />
);

const SupervisorIcon: React.FC<{ color?: string }> = ({ color = '#3A3A3A' }) => (
  <Path
    d="M7.50002 10C9.10835 10 10.4167 8.69166 10.4167 7.08333C10.4167 5.475 9.10835 4.16666 7.50002 4.16666C5.89169 4.16666 4.58335 5.475 4.58335 7.08333C4.58335 8.69166 5.89169 10 7.50002 10ZM7.50002 5.83333C8.19169 5.83333 8.75002 6.39166 8.75002 7.08333C8.75002 7.775 8.19169 8.33333 7.50002 8.33333C6.80835 8.33333 6.25002 7.775 6.25002 7.08333C6.25002 6.39166 6.80835 5.83333 7.50002 5.83333ZM7.54169 14.1667H3.97502C4.80002 13.75 6.22502 13.3333 7.50002 13.3333C7.59169 13.3333 7.69169 13.3417 7.78335 13.3417C8.06669 12.7333 8.55835 12.2333 9.15002 11.8333C8.54169 11.725 7.96669 11.6667 7.50002 11.6667C5.55002 11.6667 1.66669 12.6417 1.66669 14.5833V15.8333H7.50002V14.5833C7.50002 14.4417 7.51669 14.3 7.54169 14.1667ZM13.75 12.0833C12.2167 12.0833 9.16669 12.925 9.16669 14.5833V15.8333H18.3334V14.5833C18.3334 12.925 15.2834 12.0833 13.75 12.0833ZM14.7584 10.5667C15.3917 10.2083 15.8334 9.53333 15.8334 8.75C15.8334 7.6 14.9 6.66666 13.75 6.66666C12.6 6.66666 11.6667 7.6 11.6667 8.75C11.6667 9.53333 12.1084 10.2083 12.7417 10.5667C13.0417 10.7333 13.3834 10.8333 13.75 10.8333C14.1167 10.8333 14.4584 10.7333 14.7584 10.5667Z"
    fill={color}
  />
);

const WalkIcon: React.FC<{ color?: string }> = ({ color = '#3A3A3A' }) => (
  <Path
    d="M11.25 4.58333C12.1667 4.58333 12.9167 3.83333 12.9167 2.91667C12.9167 2 12.1667 1.25 11.25 1.25C10.3333 1.25 9.58333 2 9.58333 2.91667C9.58333 3.83333 10.3333 4.58333 11.25 4.58333ZM8.16667 7.41667L5.83333 19.1667H7.58333L9.08333 12.5L10.8333 14.1667V19.1667H12.5V12.9167L10.75 11.25L11.25 8.75C12.3333 10 14 10.8333 15.8333 10.8333V9.16667C14.25 9.16667 12.9167 8.33333 12.25 7.16667L11.4167 5.83333C10.95 5.09167 10.0167 4.79167 9.20833 5.13333L5 6.91667V10.8333H6.66667V8L8.16667 7.41667Z"
    fill={color}
  />
);

const ExploreIcon: React.FC<{ color?: string }> = ({ color = '#3A3A3A' }) => (
  <Path
    d="M10 1.66667C5.40002 1.66667 1.66669 5.40001 1.66669 10C1.66669 14.6 5.40002 18.3333 10 18.3333C14.6 18.3333 18.3334 14.6 18.3334 10C18.3334 5.40001 14.6 1.66667 10 1.66667ZM10 16.6667C6.32502 16.6667 3.33335 13.675 3.33335 10C3.33335 6.32501 6.32502 3.33334 10 3.33334C13.675 3.33334 16.6667 6.32501 16.6667 10C16.6667 13.675 13.675 16.6667 10 16.6667ZM5.41669 14.5833L11.675 11.675L14.5834 5.41667L8.32502 8.32501L5.41669 14.5833ZM10 9.08334C10.5084 9.08334 10.9167 9.49167 10.9167 10C10.9167 10.5083 10.5084 10.9167 10 10.9167C9.49169 10.9167 9.08335 10.5083 9.08335 10C9.08335 9.49167 9.49169 9.08334 10 9.08334Z"
    fill={color}
  />
);

const PersonAddIcon: React.FC<{ color?: string }> = ({ color = '#3A3A3A' }) => (
  <Path
    d="M10.8333 6.66666C10.8333 4.82499 9.34165 3.33333 7.49998 3.33333C5.65831 3.33333 4.16665 4.82499 4.16665 6.66666C4.16665 8.50833 5.65831 9.99999 7.49998 9.99999C9.34165 9.99999 10.8333 8.50833 10.8333 6.66666ZM9.16665 6.66666C9.16665 7.58333 8.41665 8.33333 7.49998 8.33333C6.58331 8.33333 5.83331 7.58333 5.83331 6.66666C5.83331 5.74999 6.58331 4.99999 7.49998 4.99999C8.41665 4.99999 9.16665 5.74999 9.16665 6.66666ZM0.833313 15V16.6667H14.1666V15C14.1666 12.7833 9.72498 11.6667 7.49998 11.6667C5.27498 11.6667 0.833313 12.7833 0.833313 15ZM2.49998 15C2.66665 14.4083 5.24998 13.3333 7.49998 13.3333C9.74165 13.3333 12.3166 14.4 12.5 15H2.49998ZM16.6666 12.5V9.99999H19.1666V8.33333H16.6666V5.83333H15V8.33333H12.5V9.99999H15V12.5H16.6666Z"
    fill={color}
  />
);

const InventoryIcon: React.FC<{ color?: string }> = ({ color = '#3A3A3A' }) => (
  <Path
    d="M2.25 4H4.25V7H14.25V4H16.25V9H18.25V4C18.25 2.9 17.35 2 16.25 2H12.07C11.65 0.84 10.55 0 9.25 0C7.95 0 6.85 0.84 6.43 2H2.25C1.15 2 0.25 2.9 0.25 4V18C0.25 19.1 1.15 20 2.25 20H8.25V18H2.25V4ZM9.25 2C9.8 2 10.25 2.45 10.25 3C10.25 3.55 9.8 4 9.25 4C8.7 4 8.25 3.55 8.25 3C8.25 2.45 8.7 2 9.25 2Z M18.25 10.5L12.76 16L9.75 13L8.25 14.5L12.76 19L19.75 12L18.25 10.5Z"
    fill={color}
  />
);

const PasteIcon: React.FC<{ color?: string }> = ({ color = '#3A3A3A' }) => (
  <Path
    d="M15.8333 1.66667H12.35C12 0.7 11.0833 0 10 0C8.91667 0 8 0.7 7.65 1.66667H4.16667C3.25 1.66667 2.5 2.41667 2.5 3.33333V16.6667C2.5 17.5833 3.25 18.3333 4.16667 18.3333H15.8333C16.75 18.3333 17.5 17.5833 17.5 16.6667V3.33333C17.5 2.41667 16.75 1.66667 15.8333 1.66667ZM10 1.66667C10.4583 1.66667 10.8333 2.04167 10.8333 2.5C10.8333 2.95833 10.4583 3.33333 10 3.33333C9.54167 3.33333 9.16667 2.95833 9.16667 2.5C9.16667 2.04167 9.54167 1.66667 10 1.66667ZM15.8333 16.6667H4.16667V3.33333H5.83333V5.83333H14.1667V3.33333H15.8333V16.6667Z"
    fill={color}
  />
);

const VerificationIcon: React.FC<{ color?: string }> = ({ color = '#3A3A3A' }) => (
  <Path
    d="M13.6 6.88L12.18 5.46L7.94 9.71L5.82 7.58L4.4 9L7.94 12.54L13.6 6.88Z M16 0H2C0.89 0 0 0.9 0 2V14C0 15.1 0.89 16 2 16H16C17.1 16 18 15.1 18 14V2C18 0.9 17.11 0 16 0ZM16 14H2V4H16V14Z"
    fill={color}
  />
);

const AnalyticsIcon: React.FC<{ color?: string }> = ({ color = '#3A3A3A' }) => (
  <Path
    d="M3 17H5V11H3V17ZM7 17H9V7H7V17ZM11 17H13V13H11V17ZM15 17H17V4H15V17ZM2 19H18V21H2V19Z"
    fill={color}
  />
);

const TrendingIcon: React.FC<{ color?: string }> = ({ color = '#3A3A3A' }) => (
  <Path
    d="M13.3334 5L15.2417 6.90833L11.175 10.975L7.84169 7.64167L1.66669 13.825L2.84169 15L7.84169 10L11.175 13.3333L16.425 8.09167L18.3334 10V5H13.3334Z"
    fill={color}
  />
);

const PerfectTruckIcon: React.FC<{ color?: string }> = ({ color = '#3A3A3A' }) => (
  <Path
    d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zm-.5 1.5l1.96 2.5H17V9.5h2.5zM6 18c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm2.22-3c-.55-.61-1.33-1-2.22-1s-1.67.39-2.22 1H3V6h12v9H8.22zM18 18c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1z"
    fill={color}
  />
);

interface HarborIconProps {
  color?: string;
}

// Harbor anchor icon — designed in 24x24 space to match other sidebar icons
const HarborIcon: React.FC<HarborIconProps> = ({ color = '#3A3A3A' }) => (
  <G
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {/* Ring */}
    <Circle cx="12" cy="5" r="2.1" />

    {/* Shank */}
    <Line x1="12" y1="7.1" x2="12" y2="19" />

    {/* Stock */}
    <Line x1="8" y1="10" x2="16" y2="10" />

    {/* Curved Flukes */}
    <Path d="M6 14A6 6 0 0 0 18 14" />
  </G>
);

const AdminIcon = ({ color = '#3A3A3A' }: { color?: string }) => (
  <Path
    d="M14.2994 9.15C14.3328 8.88333 14.3578 8.61667 14.3578 8.33333C14.3578 8.05 14.3328 7.78333 14.2994 7.51667L16.0578 6.14167C16.2161 6.01667 16.2578 5.79167 16.1578 5.60833L14.4911 2.725C14.4161 2.59167 14.2744 2.51667 14.1244 2.51667C14.0744 2.51667 14.0244 2.525 13.9828 2.54167L11.9078 3.375C11.4744 3.04167 11.0078 2.76667 10.4994 2.55833L10.1828 0.35C10.1578 0.15 9.98278 0 9.77444 0H6.44111C6.23278 0 6.05778 0.15 6.03278 0.35L5.71611 2.55833C5.20778 2.76667 4.74111 3.05 4.30778 3.375L2.23278 2.54167C2.18278 2.525 2.13278 2.51667 2.08278 2.51667C1.94111 2.51667 1.79944 2.59167 1.72444 2.725L0.0577763 5.60833C-0.050557 5.79167 -0.000556918 6.01667 0.157776 6.14167L1.91611 7.51667C1.88278 7.78333 1.85778 8.05833 1.85778 8.33333C1.85778 8.60833 1.88278 8.88333 1.91611 9.15L0.157776 10.525C-0.000556918 10.65 -0.0422237 10.875 0.0577763 11.0583L1.72444 13.9417C1.79944 14.075 1.94111 14.15 2.09111 14.15C2.14111 14.15 2.19111 14.1417 2.23278 14.125L4.30778 13.2917C4.74111 13.625 5.20778 13.9 5.71611 14.1083L6.03278 16.3167C6.05778 16.5167 6.23278 16.6667 6.44111 16.6667H9.77444C9.98278 16.6667 10.1578 16.5167 10.1828 16.3167L10.4994 14.1083C11.0078 13.9 11.4744 13.6167 11.9078 13.2917L13.9828 14.125C14.0328 14.1417 14.0828 14.15 14.1328 14.15C14.2744 14.15 14.4161 14.075 14.4911 13.9417L16.1578 11.0583C16.2578 10.875 16.2161 10.65 16.0578 10.525L14.2994 9.15ZM12.6494 7.725C12.6828 7.98333 12.6911 8.15833 12.6911 8.33333C12.6911 8.50833 12.6744 8.69167 12.6494 8.94167L12.5328 9.88333L13.2744 10.4667L14.1744 11.1667L13.5911 12.175L12.5328 11.75L11.6661 11.4L10.9161 11.9667C10.5578 12.2333 10.2161 12.4333 9.87444 12.575L8.99111 12.9333L8.85778 13.875L8.69111 15H7.52444L7.36611 13.875L7.23278 12.9333L6.34944 12.575C5.99111 12.425 5.65778 12.2333 5.32444 11.9833L4.56611 11.4L3.68278 11.7583L2.62444 12.1833L2.04111 11.175L2.94111 10.475L3.68278 9.89167L3.56611 8.95C3.54111 8.69167 3.52444 8.5 3.52444 8.33333C3.52444 8.16667 3.54111 7.975 3.56611 7.725L3.68278 6.78333L2.94111 6.2L2.04111 5.5L2.62444 4.49167L3.68278 4.91667L4.54944 5.26667L5.29944 4.7C5.65778 4.43333 5.99944 4.23333 6.34111 4.09167L7.22444 3.73333L7.35778 2.79167L7.52444 1.66667H8.68278L8.84111 2.79167L8.97444 3.73333L9.85778 4.09167C10.2161 4.24167 10.5494 4.43333 10.8828 4.68333L11.6411 5.26667L12.5244 4.90833L13.5828 4.48333L14.1661 5.49167L13.2744 6.2L12.5328 6.78333L12.6494 7.725ZM8.10778 5C6.26611 5 4.77444 6.49167 4.77444 8.33333C4.77444 10.175 6.26611 11.6667 8.10778 11.6667C9.94944 11.6667 11.4411 10.175 11.4411 8.33333C11.4411 6.49167 9.94944 5 8.10778 5ZM8.10778 10C7.19111 10 6.44111 9.25 6.44111 8.33333C6.44111 7.41667 7.19111 6.66667 8.10778 6.66667C9.02444 6.66667 9.77444 7.41667 9.77444 8.33333C9.77444 9.25 9.02444 10 8.10778 10Z"
    fill={color}
  />
);

const FolderIcon = ({ color = '#3A3A3A' }: { color?: string }) => (
  <Path
    d="M16.6667 5.00001H10L8.33335 3.33334H3.33335C2.41669 3.33334 1.67502 4.08334 1.67502 5.00001L1.66669 15C1.66669 15.9167 2.41669 16.6667 3.33335 16.6667H16.6667C17.5834 16.6667 18.3334 15.9167 18.3334 15V6.66668C18.3334 5.75001 17.5834 5.00001 16.6667 5.00001ZM16.6667 15H3.33335V6.66668H16.6667V15Z"
    fill={color}
  />
);

const MailIcon = ({ color = '#3A3A3A' }: { color?: string }) => (
  <Path
    d="M18.3334 4.99998C18.3334 4.08331 17.5834 3.33331 16.6667 3.33331H3.33335C2.41669 3.33331 1.66669 4.08331 1.66669 4.99998V15C1.66669 15.9166 2.41669 16.6666 3.33335 16.6666H16.6667C17.5834 16.6666 18.3334 15.9166 18.3334 15V4.99998ZM16.6667 4.99998L10 9.15831L3.33335 4.99998H16.6667ZM16.6667 15H3.33335V6.66665L10 10.8333L16.6667 6.66665V15Z"
    fill={color}
  />
);

const ActionPlanIcon = ({ color = '#3A3A3A' }: { color?: string }) => (
  <Path d="M19.1667 6.66667C19.1667 7.58333 18.4167 8.33333 17.5 8.33333C17.35 8.33333 17.2084 8.31667 17.075 8.275L14.1084 11.2333C14.15 11.3667 14.1667 11.5167 14.1667 11.6667C14.1667 12.5833 13.4167 13.3333 12.5 13.3333C11.5834 13.3333 10.8334 12.5833 10.8334 11.6667C10.8334 11.5167 10.85 11.3667 10.8917 11.2333L8.76671 9.10833C8.63337 9.15 8.48337 9.16667 8.33337 9.16667C8.18337 9.16667 8.03337 9.15 7.90004 9.10833L4.10837 12.9083C4.15004 13.0417 4.16671 13.1833 4.16671 13.3333C4.16671 14.25 3.41671 15 2.50004 15C1.58337 15 0.833374 14.25 0.833374 13.3333C0.833374 12.4167 1.58337 11.6667 2.50004 11.6667C2.65004 11.6667 2.79171 11.6833 2.92504 11.725L6.72504 7.93333C6.68337 7.8 6.66671 7.65 6.66671 7.5C6.66671 6.58333 7.41671 5.83333 8.33337 5.83333C9.25004 5.83333 10 6.58333 10 7.5C10 7.65 9.98337 7.8 9.94171 7.93333L12.0667 10.0583C12.2 10.0167 12.35 10 12.5 10C12.65 10 12.8 10.0167 12.9334 10.0583L15.8917 7.09167C15.85 6.95833 15.8334 6.81667 15.8334 6.66667C15.8334 5.75 16.5834 5 17.5 5C18.4167 5 19.1667 5.75 19.1667 6.66667Z" fill={color} />
);

const StandUpIcon = ({ color = '#3A3A3A' }: { color?: string }) => (
  <Path d="M17.3667 3.51634C17.325 3.41634 17.275 3.32467 17.2167 3.23301C17.1 3.05801 16.9417 2.89967 16.7667 2.78301C16.675 2.72467 16.5833 2.67467 16.4833 2.63301C16.2833 2.54967 16.0667 2.49967 15.8333 2.49967H15V0.833008H13.3333V2.49967H6.66667V0.833008H5V2.49967H4.16667C3.81667 2.49967 3.5 2.60801 3.23333 2.78301C3.05833 2.89967 2.9 3.05801 2.78333 3.23301C2.725 3.32467 2.675 3.41634 2.63333 3.51634C2.55 3.71634 2.5 3.93301 2.5 4.16634V15.833C2.5 16.7497 3.24167 17.4997 4.16667 17.4997H15.8333C16.0667 17.4997 16.2833 17.4497 16.4833 17.3663C16.5833 17.3247 16.675 17.2747 16.7667 17.2163C16.9417 17.0997 17.1 16.9413 17.2167 16.7663C17.3917 16.4997 17.5 16.1747 17.5 15.833V4.16634C17.5 3.93301 17.45 3.71634 17.3667 3.51634ZM4.16667 15.833V4.16634H15.8333V15.833H4.16667ZM10 10.733C8.30833 10.733 5 11.633 5 13.7163V14.9997H15V13.7247C15 11.633 11.6917 10.733 10 10.733ZM6.925 13.333C7.5 12.8663 8.90833 12.3997 10 12.3997C11.0917 12.3997 12.5083 12.8663 13.075 13.333H6.925ZM10 9.99967C11.375 9.99967 12.5 8.87467 12.5 7.49967C12.5 6.12467 11.375 4.99967 10 4.99967C8.625 4.99967 7.5 6.12467 7.5 7.49967C7.5 8.87467 8.625 9.99967 10 9.99967ZM10 6.66634C10.4583 6.66634 10.8333 7.04134 10.8333 7.49967C10.8333 7.95801 10.4583 8.33301 10 8.33301C9.54167 8.33301 9.16667 7.95801 9.16667 7.49967C9.16667 7.04134 9.54167 6.66634 10 6.66634Z" fill={color}/>
);

const PepsiCoStickyLogo = () => (
  <Svg width="40" height="40" viewBox="0 0 40 40" fill="none">
    <Path d="M0 20C0 8.9543 8.9543 0 20 0C31.0457 0 40 8.9543 40 20C40 31.0457 31.0457 40 20 40C8.9543 40 0 31.0457 0 20Z" fill="#005CBC"/>
    <Path fillRule="evenodd" clipRule="evenodd" d="M32.0928 26.0325C32.4648 17.8317 25.7557 8.59566 16.6093 7.45554L16.6249 7.34201C25.6419 7.34201 32.5691 16.1418 32.5691 23.0841C32.5605 24.5290 32.4517 25.4252 32.2458 26.0747L32.0928 26.0325ZM31.5464 27.0879C30.9321 27.8476 30.1920 28.6257 29.3508 29.3641C27.6825 19.4904 20.6664 10.8832 16.0928 8.48269L15.9887 8.55757C20.6611 12.6568 26.5324 20.9107 28.8275 29.7511C27.6286 30.6451 26.2802 31.4016 24.8197 32.0127C20.2793 26.4549 16.1946 16.0491 14.8093 9.54238L14.6683 9.58695C14.7000 15.8952 18.9919 26.7890 24.5436 32.2297C23.3030 32.7615 22.0134 33.1370 20.6968 33.3499C15.4979 31.2705 11.8216 24.2636 11.8216 17.1603C11.8216 12.5173 13.1572 9.64692 13.4225 9.05542L13.3119 9.01631C13.0123 9.50121 11.0404 12.3295 11.0404 17.1100C11.0404 25.1397 14.7899 31.4198 19.7789 33.4958C17.9078 33.7027 16.0476 33.6137 14.4617 33.2307L14.4253 33.3539C14.9300 33.5616 16.7397 34.3618 19.5082 34.3618C25.7463 34.3618 29.9975 30.2712 31.6682 27.1615L31.5464 27.0879Z" fill="white"/>
    <Path fillRule="evenodd" clipRule="evenodd" d="M18.8867 4.44439C14.4664 4.44439 8.60335 8.04798 8.60335 11.3392C8.60335 12.6206 9.70150 13.6895 11.9936 13.6895C17.3035 13.6895 22.2377 9.84003 22.2377 6.85229C22.2377 5.27147 20.8038 4.44439 18.8867 4.44439V4.44439ZM20.7769 6.76649C20.7769 9.28020 16.1849 12.3618 12.3176 12.3618C10.6216 12.3618 9.72676 11.6579 9.72676 10.4971C9.72676 7.94434 14.3782 4.91140 17.9936 4.91140C20.3263 4.91140 20.7769 6.17441 20.7769 6.76649V6.76649Z" fill="white"/>
    <Path fillRule="evenodd" clipRule="evenodd" d="M6.67177 12.8639C6.54011 13.1031 6.01591 14.1844 6.01591 15.3140C6.01591 17.4214 8.01529 19.3636 11.7309 19.3636C18.7920 19.3636 26.9840 13.8638 26.9840 8.75420C26.9840 6.46475 25.040 5.21264 23.4961 4.95211L23.4690 5.04878C23.9105 5.20155 25.4205 5.98398 25.4205 8.03542C25.4205 12.1078 18.3096 17.6521 11.1201 17.6521C8.35051 17.6521 6.61849 16.2164 6.61849 14.1270C6.61849 13.4646 6.74620 13.0290 6.78235 12.9030L6.67177 12.8639Z" fill="white"/>
    <Path fillRule="evenodd" clipRule="evenodd" d="M29.9133 9.13564C30.1363 9.30860 31.3041 10.3203 31.3041 12.2549C31.3041 18.8759 21.5720 25.3809 12.7505 25.3809C7.59665 25.3809 5.21775 22.6976 5.33669 20.2869H5.43244C5.63273 21.5305 7.31583 23.7576 11.8539 23.7576C20.5564 23.7576 30.2751 17.0266 30.2751 11.1292C30.2751 10.1175 30.0137 9.45557 29.8663 9.17839L29.9133 9.13564Z" fill="white"/>
    <Path fillRule="evenodd" clipRule="evenodd" d="M33.2054 15.3144C33.2472 15.4554 33.4579 16.0901 33.4579 17.0952C33.4579 24.4394 23.5071 30.7683 14.8467 30.7683C10.3988 30.7683 8.06428 28.4873 7.58466 27.5282L7.66524 27.4697C8.89207 28.6069 11.3354 29.4558 14.1541 29.4558C21.7977 29.4558 33.2593 23.3402 33.1576 15.3269L33.2054 15.3144Z" fill="white"/>
  </Svg>
);

const SubmenuArrowIcon = ({ isExpanded, color = '#616161' }: { isExpanded: boolean; color?: string }) => (
  <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <Path
      d={isExpanded
        ? "M4.94 10.2733L8 7.22001L11.06 10.2733L12 9.33334L8 5.33334L4 9.33334L4.94 10.2733Z"
        : "M4.94 5.72668L8 8.78002L11.06 5.72668L12 6.66668L8 10.6667L4 6.66668L4.94 5.72668Z"
      }
      fill={color}
    />
  </Svg>
);

const TooltipLockIcon = () => (
  <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <Path
      d="M12.6667 7.33333H12V6C12 3.79333 10.2067 2 8 2C5.79333 2 4 3.79333 4 6V7.33333H3.33333C2.6 7.33333 2 7.93333 2 8.66667V13.3333C2 14.0667 2.6 14.6667 3.33333 14.6667H12.6667C13.4 14.6667 14 14.0667 14 13.3333V8.66667C14 7.93333 13.4 7.33333 12.6667 7.33333ZM8 11.3333C7.26667 11.3333 6.66667 10.7333 6.66667 10C6.66667 9.26667 7.26667 8.66667 8 8.66667C8.73333 8.66667 9.33333 9.26667 9.33333 10C9.33333 10.7333 8.73333 11.3333 8 11.3333ZM10.7333 7.33333H5.26667V6C5.26667 4.49333 6.49333 3.26667 8 3.26667C9.50667 3.26667 10.7333 4.49333 10.7333 6V7.33333Z"
      fill="#FFFFFF"
    />
  </Svg>
);

export default function DLOSidebar({ activeScreen, onNavigate, isVisible = true }: DLOSidebarProps) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const styles = useMemo(() => createSidebarStyles(isDark), [isDark]);

  const getIconColor = (isActive: boolean, isHovered: boolean, isDisabled: boolean) => {
    if (isDisabled) return isDark ? '#555555' : '#BDBDBD';
    if (isActive || isHovered) return isDark ? '#3D8BFF' : '#005CBC';
    return isDark ? '#B0B0B0' : '#3A3A3A';
  };

  const renderIconChild = (child: any, iconColor: string): any => {
    if (!child) return child;
    if (React.isValidElement(child)) {
      if (child.type === Path || child.type === Circle || child.type === Line || child.type === G) {
        const props: any = { ...child.props };
        if (props.fill && props.fill !== 'none') {
          props.fill = iconColor;
        }
        if (props.stroke && props.stroke !== 'none') {
          props.stroke = iconColor;
        }
        if (props.children) {
          props.children = React.Children.map(props.children, c => renderIconChild(c, iconColor));
        }
        return React.cloneElement(child, props);
      }
      return React.cloneElement(child as React.ReactElement<any>, {
        color: iconColor,
        ...(child.props?.children ? {
          children: React.Children.map(child.props.children, c => renderIconChild(c, iconColor))
        } : {})
      });
    }
    return child;
  };
  const [screenWidth, setScreenWidth] = useState(Dimensions.get('window').width);
  const [isDesktop, setIsDesktop] = useState(Dimensions.get('window').width >= 1024);
  const [isTablet, setIsTablet] = useState(Dimensions.get('window').width >= 768 && Dimensions.get('window').width < 1024);
  const [isExpanded, setIsExpanded] = useState(false);
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [isDockManagerExpanded, setIsDockManagerExpanded] = useState(false);
  const [isSidebarHovered, setIsSidebarHovered] = useState(false);
  const sidebarRef = React.useRef<View>(null);

  const getNavigationTarget = (id: string): string => {
    switch (id) {
      case 'home':
        return 'Home';
      case 'observations':
        return 'Observations';
      case 'shift-handover':
        return 'Shift Handover';
      case 'employee-update':
        return 'Employee Update';
      case 'leadership-walk':
        return 'Leadership Walk';
      case 'sparc-walk':
        return 'SPARC Walk';
      case 'nho':
        return 'NHO';
      case 'people-hub':
        return 'People Hub';
      case 'racking-inspection':
        return 'Racking Inspection';
      case 'quality-audit':
        return 'Quality Audit';
      case 'site-evaluation':
        return 'Site Evaluation';
      case 'analytics':
        return 'Analytics';
      case 'action-plan':
        return 'Action Plan';
      case 'stand-up':
        return 'Stand Up';
      case 'labor-optimization':
        return 'DLODashboard';
      case 'perfect-truck':
        return 'Perfect Truck';
      case 'harbor':
        return 'Harbor';
      case 'admin-dashboard':
        return 'Admin Dashboard';
      case 'ticketing-portal':
        return 'Ticketing Portal - Contact Us';
      case 'contact-feedback':
        return 'Contact us/Feedback';
      default:
        return id;
    }
  };

  const trackSidebarIconClick = (args: {
    sidebarView: 'collapsed' | 'expanded';
    iconId: string;
    iconLabel?: string;
    action: 'navigate' | 'expand' | 'collapse' | 'toggle_submenu';
    targetScreen?: string;
    isDisabled?: boolean;
    hasSubmenu?: boolean;
  }) => {
    try {
      FullStory.event('DLO Sidebar Icon Clicked', {
        module: 'DLO',
        sidebar_view: args.sidebarView,
        action: args.action,
        icon_id: args.iconId,
        icon_label: args.iconLabel,
        target_screen: args.targetScreen,
        active_screen: activeScreen,
        is_disabled: !!args.isDisabled,
        has_submenu: !!args.hasSubmenu,
        platform: Platform.OS,
        screen_width: screenWidth,
        is_desktop: isDesktop,
        is_tablet: isTablet,
      });
    } catch (_) {
      // no-op
    }
  };

  // Get current user's role name for role-based visibility
  const userRoleName = useSelector((state: RootState) => state.appData.userInfo?.roleName);
  const isSuperUser = userRoleName === 'Super User' || userRoleName === 'Developer';
  // Get locationLive status from appData
  const locationLive = useSelector((state: RootState) => state.appData.locationLive);


  const selectedLocation = useSelector(
    (state: RootState) => state.appData.selectedLocation
  );


  // Get module access from Redux
  const moduleAccess = useSelector(
    (state: RootState) => state.appData.moduleAccess
  );
  const userInfo = useSelector(
    (state: RootState) => state.appData.userInfo
  );

  // Helper function to check if a module has access
  const hasModuleAccess = (moduleName: string): boolean => {
    if (!moduleAccess || !Array.isArray(moduleAccess)) {
      return false;
    }
    const module = moduleAccess.find(m => m.moduleName === moduleName);
    return module ? module.hasAccess : false;
  };

  const isLiveLocation = useMemo(() => {
    const normalizedLocationLive = (locationLive || userInfo?.isLive || '').toLowerCase();
    return normalizedLocationLive === 'yes' || normalizedLocationLive === 'true';
  }, [locationLive, userInfo?.isLive]);

  const shouldShowAnalyticsHub = hasModuleAccess('Analytics');

  // Create a computed map of which sidebar items are disabled
  const disabledItems = useMemo(() => {
    const disabled: { [key: string]: boolean } = {};

    // Each sidebar item is gated by its own dedicated module name from the API
    disabled['observations'] = !hasModuleAccess('Observations');
    disabled['shift-handover'] = !hasModuleAccess('Shift Handover');
    disabled['employee-update'] = !hasModuleAccess('Employee Update');
    disabled['leadership-walk'] = !hasModuleAccess('Leadership Walk');
    disabled['sparc-walk'] = !hasModuleAccess('SPARC Walk');
    disabled['racking-inspection'] = !hasModuleAccess('Racking Inspection');
    disabled['action-plan'] = !hasModuleAccess('Action Plan');
    disabled['quality-audit'] = !hasModuleAccess('Quality Audit');
    disabled['site-evaluation'] = !hasModuleAccess('Site Evaluation');
    disabled['labor-optimization'] = !hasModuleAccess('DLO');
    disabled['nho'] = !hasModuleAccess('NHO');
    // disabled['people-hub'] = !hasModuleAccess('People Hub');
    disabled['stand-up'] = !hasModuleAccess('Stand Up');
    disabled['perfect-truck'] = !hasModuleAccess('Perfect Truck');
    disabled['harbor'] = !hasModuleAccess('Perfect Truck')|| !hasModuleAccess('Delivery RPM');
    disabled['analytics'] = !hasModuleAccess('Analytics');
    disabled['admin-dashboard'] = !hasModuleAccess('Config');
    disabled['action-plan'] = !hasModuleAccess('Action Plan');
    // Ticketing Portal is not in the API moduleAccess response — always accessible
    disabled['ticketing-portal'] = false;

    return disabled;
  }, [moduleAccess, isLiveLocation]);

  // Check if we should hide labor-optimization on iOS
  const shouldShowLaborOptimization = Platform.OS !== 'ios';

  // Check if we should hide stand-up on iOS
  const shouldShowStandUp = Platform.OS !== 'ios';

  // Map activeScreen prop to sidebar item ID
  const getActiveItemFromScreen = (screen: string): string => {
    switch (screen) {
      case 'Home':
        return 'home';
      case 'Observations':
      case 'PulseCheck': // PulseCheck is part of Observations
        return 'observations';
      case 'Shift Handover':
      case 'ShiftHandoverRedirect': // Shift Handover redirect screen
        return 'shift-handover';
      case 'Employee Update':
      case 'EmployeeUpdate': // Handle both variations
        return 'employee-update';
      case 'Leadership Walk':
      case 'LeadershipWalkRedirect': // Leadership Walk redirect screen
        return 'leadership-walk';
      case 'SPARC Walk':
        return 'sparc-walk';
      case 'NHO':
      case 'NHO Admin':
      case 'NHO Manage Employees':
      case 'NHOEmployeeProfile':
      case 'NHO Begin':
      case 'NHO PulseCheckTouchPoint':
      case 'NHO SpecificPeriodTouchPoint': // All NHO-related screens
        return 'nho';
      case 'People Hub':
        return 'people-hub';
      case 'Racking Inspection':
        return 'racking-inspection';
      case 'Quality Audit':
        return 'quality-audit';
      case 'Site Evaluation':
        return 'site-evaluation';
      case 'Analytics':
        return 'analytics';
      case 'Action Plan':
        return 'action-plan';
      case 'Stand Up':
        return 'stand-up';
      case 'Ticketing Portal':
      case 'Ticketing Portal - Contact Us':
      case 'Submit Ticket':
      case 'My Tickets':
      case 'Sustain Queue':
      case 'Ticket Detail':
      case 'Notifications & Comms':
        return 'ticketing-portal';
      case 'Contact us/Feedback':
        return 'contact-feedback';
      case 'Admin Dashboard':
        return 'admin-dashboard';
      case 'Perfect Truck':
        return 'perfect-truck';
      case 'Harbor':
        return 'harbor';
      case 'Delivery RPM':
        return 'harbor';
      case 'DLODashboard':
      case 'DLO': // Handle both DLO and DLODashboard
      case 'DLOLocationSetting': // Location settings is part of labor optimization
      case 'DLOCreateSchedule': // Create schedule screen
      case 'DLOScheduleSetting': // Schedule settings screen
        return 'labor-optimization';
      default:
        return 'home';
    }
  };

  const activeItem = getActiveItemFromScreen(activeScreen);
  const disabledTooltipMessage = 'Module only available for Site Supervisor level access or above';

  const renderDisabledTooltipContent = () => (
    <View style={styles.disabledTooltipContent}>
      <View style={styles.disabledTooltipIconWrapper}>
        <TooltipLockIcon />
      </View>
      <Text style={styles.disabledTooltipText}>{disabledTooltipMessage}</Text>
    </View>
  );

  const getExpandedTooltipProps = (
    itemId: string,
    isDisabled = false,
    titleOverride?: string,
    contentOverride?: string,
  ) => {
    if (isDisabled) {
      return {
        content: disabledTooltipMessage,
        customContent: renderDisabledTooltipContent(),
        title: undefined,
      };
    }

    const tooltipContent = getTooltipContent(itemId);
    return {
      title: titleOverride ?? tooltipContent.title,
      content: contentOverride ?? tooltipContent.description,
      customContent: undefined,
    };
  };

  useEffect(() => {
    const handleLayout = () => {
      const { width } = Dimensions.get('window');
      setScreenWidth(width);
      setIsDesktop(width >= 1024);
      setIsTablet(width >= 768 && width < 1024);
    };

    const subscription = Dimensions.addEventListener('change', handleLayout);
    return () => subscription.remove();
  }, []);

  // Force hide all tooltips when cursor leaves sidebar
  useEffect(() => {
    if (Platform.OS === 'web' && !isSidebarHovered) {
      // Dispatch custom event to force hide all tooltips
      const event = new CustomEvent('dlo-sidebar-leave');
      window.dispatchEvent(event);
    }
  }, [isSidebarHovered]);

  // Clean up tooltips on navigation
  useEffect(() => {
    if (Platform.OS === 'web') {
      // Force hide tooltips when navigating
      const event = new CustomEvent('dlo-sidebar-leave');
      window.dispatchEvent(event);
    }
  }, [activeScreen]);

  // Collapsed NavigationItem for the initial sidebar
  const NavigationItem = ({ 
    id, 
    children,
    isClickable = true,
    isDisabled = false,
    onClick
  }: { 
    id: string; 
    children: React.ReactElement<any>;
    isClickable?: boolean;
    isDisabled?: boolean;
    onClick?: () => void;
  }) => {
    const isActive = isClickable && activeItem === id;
    const [isHovered, setIsHovered] = useState(false);
    const iconColor = getIconColor(isActive, isHovered, isDisabled);
    
    return (
      <Pressable
        style={({ pressed }) => [
          styles.navItem,
          isActive && styles.activeNavItem,
          (isHovered || pressed) && !isDisabled && styles.hoveredNavItem,
          isDisabled && styles.disabledNavItem
        ]}
        onPress={() => {
          if (isClickable && !isDisabled) {
            if (id === 'menu') {
              trackSidebarIconClick({
                sidebarView: 'collapsed',
                iconId: id,
                iconLabel: 'Menu',
                action: 'expand',
              });
              setIsExpanded(true);
            } else {
              trackSidebarIconClick({
                sidebarView: 'collapsed',
                iconId: id,
                iconLabel: getTooltipContent(id).title,
                action: 'navigate',
                targetScreen: getNavigationTarget(id),
              });

              setIsExpanded(false);
              console.log(`Navigating to==== ${id}`);
              // Handle navigation based on id
              switch (id) {
                case 'home':
                  onNavigate('Home');
                  break;
                case 'observations':
                  onNavigate('Observations');
                  break;
                case 'shift-handover':
                  onNavigate('Shift Handover');
                  break;
                case 'employee-update':
                  onNavigate('Employee Update');
                  break;
                case 'leadership-walk':
                  onNavigate('Leadership Walk');
                  break;
                case 'sparc-walk':
                  onNavigate('SPARC Walk');
                  break;
                case 'nho':
                  onNavigate('NHO');
                  break;
                case 'people-hub':
                  onNavigate('People Hub');
                  break;
                case 'racking-inspection':
                  onNavigate('Racking Inspection');
                  break;
                case 'quality-audit':
                  onNavigate('Quality Audit');
                  break;
                case 'site-evaluation':
                  onNavigate('Site Evaluation');
                  break;
                case 'analytics':
                  onNavigate('Analytics');
                  break;
                case 'action-plan':
                  onNavigate('Action Plan');
                  break;
                case 'stand-up':
                  onNavigate('Stand Up');
                  break;
                case 'labor-optimization':
                  onNavigate('DLODashboard');
                  break;
                case 'admin-dashboard':
                  onNavigate('Admin Dashboard');
                  break;
                case 'perfect-truck':
                  onNavigate('Perfect Truck');
                  break;
                case 'harbor':
                  onNavigate('Harbor');
                  break;
                case 'ticketing-portal':
                  onNavigate('Ticketing Portal');
                  break;
                case 'contact-feedback':
                  // Open external PowerApps URL in new tab
                  if (Platform.OS === 'web') {
                    window.open('https://apps.powerapps.com/play/e/default-42cc3295-cd0e-449c-b98e-5ce5b560c1d3/a/e2fdf8bc-ab8c-44c2-8cd3-d1b22f94de19?tenantId=42cc3295-cd0e-449c-b98e-5ce5b560c1d3#', '_blank');
                  } else {
                    Linking.openURL('https://apps.powerapps.com/play/e/default-42cc3295-cd0e-449c-b98e-5ce5b560c1d3/a/e2fdf8bc-ab8c-44c2-8cd3-d1b22f94de19?tenantId=42cc3295-cd0e-449c-b98e-5ce5b560c1d3#');
                  }
                  break;
                default:
                  onNavigate(id);
                  break;
              }
              // Close sidebar when navigating to a menu item

            }
          }
        }}
        onHoverIn={() => setIsHovered(true)}
        onHoverOut={() => setIsHovered(false)}
        disabled={isDisabled}
      >
        <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          {React.Children.map(
            children?.props?.children !== undefined ? children.props.children : children,
            (child: any) => renderIconChild(child, iconColor)
          )}
        </Svg>
      </Pressable>
    );
  };

  // Helper component for dynamic icon colors
  const DynamicIcon = ({
    isActive,
    isHovered,
    isDisabled = false,
    children
  }: {
    isActive: boolean;
    isHovered: boolean;
    isDisabled?: boolean;
    children: React.ReactNode;
  }) => {
    const iconColor = getIconColor(isActive, isHovered, isDisabled);
    const childrenToRender = (React.isValidElement(children) && (children as any).props?.children !== undefined && (children as any).type === Svg)
      ? (children as any).props.children
      : children;
    return (
      <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        {React.Children.map(childrenToRender, (child: any) => renderIconChild(child, iconColor))}
      </Svg>
    );
  };

  // Expanded NavigationItem for the expanded sidebar
  const ExpandedNavigationItem = ({
    id,
    iconChildren,
    label,
    isActive = false,
    hasSubmenu = false,
    isExpanded = false,
    isDisabled = false,
    onPress,
    children,
  }: {
    id?: string;
    iconChildren: React.ReactNode;
    label: string;
    isActive?: boolean;
    hasSubmenu?: boolean;
    isExpanded?: boolean;
    isDisabled?: boolean;
    onPress?: () => void;
    children?: React.ReactNode;
  }) => {
    const [isHovered, setIsHovered] = useState(false);
    const isItemActive = id ? activeItem === id : isActive;
    
    const handlePress = () => {
      if (id && !isDisabled) {
        trackSidebarIconClick({
          sidebarView: 'expanded',
          iconId: id,
          iconLabel: label,
          action: hasSubmenu ? 'toggle_submenu' : 'navigate',
          targetScreen: hasSubmenu ? undefined : getNavigationTarget(id),
          isDisabled,
          hasSubmenu,
        });
      }

      if (id && !hasSubmenu && !isDisabled) {
        switch (id) {
          case 'home':
            onNavigate('Home');
            break;
          case 'observations':
            onNavigate('Observations');
            break;
          case 'shift-handover':
            onNavigate('Shift Handover');
            break;
          case 'employee-update':
            onNavigate('Employee Update');
            break;
          case 'leadership-walk':
            onNavigate('Leadership Walk');
            break;
          case 'sparc-walk':
            onNavigate('SPARC Walk');
            break;
          case 'nho':
            onNavigate('NHO');
            break;
          case 'people-hub':
            onNavigate('People Hub');
            break;
          case 'racking-inspection':
            onNavigate('Racking Inspection');
            break;
          case 'quality-audit':
            onNavigate('Quality Audit');
            break;
          case 'site-evaluation':
            onNavigate('Site Evaluation');
            break;
          case 'analytics':
            onNavigate('Analytics');
            break;
          case 'action-plan':
            onNavigate('Action Plan');
            break;
          case 'stand-up':
            onNavigate('Stand Up');
            break;
          case 'labor-optimization':
            onNavigate('DLODashboard');
            break;
          case 'admin-dashboard':
            onNavigate('Admin Dashboard');
            break;
          case 'perfect-truck':
            onNavigate('Perfect Truck');
            break;
          case 'harbor':
            onNavigate('Harbor');
            break;
          case 'ticketing-portal':
            onNavigate('Ticketing Portal');
            break;
          case 'contact-feedback':
            // Open external PowerApps URL in new tab
            if (Platform.OS === 'web') {
              window.open('https://apps.powerapps.com/play/e/default-42cc3295-cd0e-449c-b98e-5ce5b560c1d3/a/e2fdf8bc-ab8c-44c2-8cd3-d1b22f94de19?tenantId=42cc3295-cd0e-449c-b98e-5ce5b560c1d3#', '_blank');
            } else {
              Linking.openURL('https://apps.powerapps.com/play/e/default-42cc3295-cd0e-449c-b98e-5ce5b560c1d3/a/e2fdf8bc-ab8c-44c2-8cd3-d1b22f94de19?tenantId=42cc3295-cd0e-449c-b98e-5ce5b560c1d3#');
            }
            break;
          default:
            onNavigate(id);
            break;
        }
        // Close sidebar when navigating to a menu item
        setIsExpanded(false);
      }
      if (onPress && !isDisabled) {
        onPress();
      }
    };
    
    return (
      <View>
        <Pressable
          style={({ pressed }) => [
            styles.expandedNavItem,
            isItemActive && styles.activeExpandedNavItem,
            (isHovered || pressed) && !isDisabled && styles.hoveredExpandedNavItem,
            isDisabled && styles.disabledExpandedNavItem
          ]}
          onPress={handlePress}
          onHoverIn={() => setIsHovered(true)}
          onHoverOut={() => setIsHovered(false)}
          disabled={isDisabled}
        >
          <View style={styles.expandedNavItemContent}>
            <DynamicIcon isActive={isItemActive} isHovered={isHovered} isDisabled={isDisabled}>
              {iconChildren}
            </DynamicIcon>
            <Text style={[
              styles.expandedNavItemLabel,
              (isItemActive || isHovered) && !isDisabled && { color: isDark ? '#3D8BFF' : '#005CBC' },
              isDisabled && { color: isDark ? '#555555' : '#BDBDBD' }
            ]}>
              {label}
            </Text>
          </View>
          {hasSubmenu && (
            <SubmenuArrowIcon isExpanded={isExpanded} color={isDark ? '#B0B0B0' : '#616161'} />
          )}
        </Pressable>
        {children}
      </View>
    );
  };

  const SubmenuItem = ({ label, onPress }: { label: string; onPress?: () => void }) => {
    return (
      <Pressable style={styles.submenuItem} onPress={onPress}>
        <View style={styles.submenuItemContent}>
          <Text style={styles.submenuItemLabel}>{label}</Text>
        </View>
      </Pressable>
    );
  };

  // Handle responsive visibility (media query-like behavior)
  // Show sidebar on desktop (1024px+) and tablet landscape (768px+)
  const shouldShowSidebar = isVisible && screenWidth >= 768;

  if (!shouldShowSidebar) return null;

  // Expanded Sidebar Content
  const ExpandedSidebarContent = () => {
    return (
      <ScrollView 
        style={{ flex: 1, width: '100%' }}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={styles.scrollContent}
      >
        {/* Menu List */}
        <View style={styles.expandedMenuList}>
              {/* Close Button */}
              <DLOTooltip title="Close" content="Collapse sidebar to icon view" position="right" trigger="hover" fontSize={14}>
                <ExpandedNavigationItem
                  iconChildren={<CloseIcon />}
                  label="Close"
                  onPress={() => {
                    trackSidebarIconClick({
                      sidebarView: 'expanded',
                      iconId: 'close',
                      iconLabel: 'Close',
                      action: 'collapse',
                    });
                    setIsExpanded(false);
                  }}
                />
              </DLOTooltip>

              {/* Home */}
              <DLOTooltip 
                title={getTooltipContent('home').title}
                content={getTooltipContent('home').description}
                position="right" 
                trigger="hover"
                fontSize={14}
              >
                <ExpandedNavigationItem
                  id="home"
                  iconChildren={<HomeIcon />}
                  label="Dashboard"
                  isDisabled={disabledItems['home']}
                />
              </DLOTooltip>

              {(
                <>
                  {/* Observations */}
                  <DLOTooltip 
                    {...getExpandedTooltipProps('observations', disabledItems['observations'])}
                    position="right" 
                    trigger="hover"
                    fontSize={14}
                  >
                    <ExpandedNavigationItem
                      id="observations"
                      iconChildren={<VisibilityIcon />}
                      label="Observations"
                      isDisabled={disabledItems['observations']}
                    />
                  </DLOTooltip>

                  {/* Shift Handover */}
                  <DLOTooltip 
                    {...getExpandedTooltipProps('shift-handover', disabledItems['shift-handover'])}
                    position="right" 
                    trigger="hover"
                    fontSize={14}
                  >
                    <ExpandedNavigationItem
                      id="shift-handover"
                      iconChildren={<SwapIcon />}
                      label="Shift Handover"
                      isDisabled={disabledItems['shift-handover']}
                    />
                  </DLOTooltip>

                  {/* Leadership Walk */}
                  <DLOTooltip 
                    {...getExpandedTooltipProps('leadership-walk', disabledItems['leadership-walk'])}
                    position="right" 
                    trigger="hover"
                    fontSize={14}
                  >
                    <ExpandedNavigationItem
                      id="leadership-walk"
                      iconChildren={<WalkIcon />}
                      label="Leadership Walk"
                      isDisabled={disabledItems['leadership-walk']}
                    />
                  </DLOTooltip>

                  {/* SPARC Walk */}
                  <DLOTooltip 
                    {...getExpandedTooltipProps('sparc-walk', disabledItems['sparc-walk'])}
                    position="right" 
                    trigger="hover"
                    fontSize={14}
                  >
                    <ExpandedNavigationItem
                      id="sparc-walk"
                      iconChildren={<ExploreIcon />}
                      label="SPARC Walk"
                      isDisabled={disabledItems['sparc-walk']}
                    />
                  </DLOTooltip>

                  {/* NHO */}
                  <DLOTooltip 
                    {...getExpandedTooltipProps('nho', disabledItems['nho'])}
                    position="right" 
                    trigger="hover"
                    fontSize={14}
                  >
                    <ExpandedNavigationItem
                      id="nho"
                      iconChildren={<PersonAddIcon />}
                      label="NHO"
                      isDisabled={disabledItems['nho']}
                    />
                  </DLOTooltip>

                  {/* People Hub */}
                  <DLOTooltip
                    {...getExpandedTooltipProps('people-hub', disabledItems['people-hub'])}
                    position="right"
                    trigger="hover"
                    fontSize={14}
                  >
                    <ExpandedNavigationItem
                      id="people-hub"
                      iconChildren={<SupervisorIcon />}
                      label="People Hub"
                      // isDisabled={disabledItems['people-hub']}
                    />
                  </DLOTooltip>

                  {/* Racking Inspection */}
                  <DLOTooltip 
                    {...getExpandedTooltipProps('racking-inspection', disabledItems['racking-inspection'])}
                    position="right" 
                    trigger="hover"
                    fontSize={14}
                  >
                    <ExpandedNavigationItem
                      id="racking-inspection"
                      iconChildren={<InventoryIcon />}
                      label="Racking Inspection"
                      isDisabled={disabledItems['racking-inspection']}
                    />
                  </DLOTooltip>

                  {/* Quality Audit */}
                  <DLOTooltip 
                    {...getExpandedTooltipProps('quality-audit', disabledItems['quality-audit'])}
                    position="right" 
                    trigger="hover"
                    fontSize={14}
                  >
                    <ExpandedNavigationItem
                      id="quality-audit"
                      iconChildren={<PasteIcon />}
                      label="Quality Audit"
                      isDisabled={disabledItems['quality-audit']}
                    />
                  </DLOTooltip>

                  {/* Site Evaluation */}
                  <DLOTooltip 
                    {...getExpandedTooltipProps('site-evaluation', disabledItems['site-evaluation'])}
                    position="right" 
                    trigger="hover"
                    fontSize={14}
                  >
                    <ExpandedNavigationItem
                      id="site-evaluation"
                      iconChildren={<VerificationIcon />}
                      label="Site Evaluation"
                      isDisabled={disabledItems['site-evaluation']}
                    />
                  </DLOTooltip>

                  {/* Analytics */}
                  {shouldShowAnalyticsHub && (
                    <DLOTooltip
                      {...getExpandedTooltipProps('analytics', disabledItems['analytics'])}
                      position="right"
                      trigger="hover"
                      fontSize={14}
                    >
                      <ExpandedNavigationItem
                        id="analytics"
                        iconChildren={<AnalyticsIcon />}
                        label="Analytics"
                        isDisabled={disabledItems['analytics']}
                      />
                    </DLOTooltip>
                  )}

                  {/* Action Plan */}
                  <DLOTooltip
                    {...getExpandedTooltipProps('action-plan', disabledItems['action-plan'], undefined, getTooltipContent('action-plan').title)}
                    position="right"
                    trigger="hover"
                    fontSize={14}
                  >
                    <ExpandedNavigationItem
                      id="action-plan"
                      iconChildren={<ActionPlanIcon />}
                      label="Action Plan"
                      isDisabled={disabledItems['action-plan']}
                    />
                  </DLOTooltip>

                  {/* Stand Up - Hidden on iOS */}
                  {shouldShowStandUp && (
                    <DLOTooltip
                      {...getExpandedTooltipProps('stand-up', disabledItems['stand-up'], undefined, getTooltipContent('stand-up').title)}
                      position="right"
                      trigger="hover"
                      fontSize={14}
                    >
                      <ExpandedNavigationItem
                        id="stand-up"
                        iconChildren={<StandUpIcon />}
                        label="Stand Up"
                        isDisabled={disabledItems['stand-up']}
                      />
                    </DLOTooltip>
                  )}

                  {/* Labor Optimization - Only show on web */}
                  {shouldShowLaborOptimization && (
                    <DLOTooltip
                      {...getExpandedTooltipProps('labor-optimization', disabledItems['labor-optimization'])}
                      position="right"
                      trigger="hover"
                      fontSize={14}
                    >
                      <ExpandedNavigationItem
                        id="labor-optimization"
                        iconChildren={<TrendingIcon />}
                        label="Labor Optimization"
                        isDisabled={disabledItems['labor-optimization']}
                      />
                    </DLOTooltip>
                  )}
                </>
              )}

              {/* Dock Manager - Submenu - Commented out for later activation */}
              {/* <DLOTooltip 
                title="Dock Manager"
                content="Manage dock operations, logistics, and transportation processes including route reviews, trailer inspections, and asset management" 
                position="right" 
                trigger="hover"
              >
                <ExpandedNavigationItem
                  iconChildren={<FolderIcon />}
                  label="Dock Manager"
                  hasSubmenu={true}
                  isExpanded={isDockManagerExpanded}
                  onPress={() => setIsDockManagerExpanded(!isDockManagerExpanded)}
                >
                  {isDockManagerExpanded && (
                    <View style={styles.submenuContainer}>
                      <SubmenuItem label="Route Review" onPress={() => onNavigate('route-review')} />
                      <SubmenuItem label="Trailer Inspection" onPress={() => onNavigate('trailer-inspection')} />
                      <SubmenuItem label="Perfect Truck" onPress={() => onNavigate('perfect-truck')} />
                      <SubmenuItem label="Check In" onPress={() => onNavigate('check-in')} />
                      <SubmenuItem label="Check the Checker" onPress={() => onNavigate('check-the-checker')} />
                      <SubmenuItem label="Haulbacks" onPress={() => onNavigate('haulbacks')} />
                      <SubmenuItem label="Seal Log" onPress={() => onNavigate('seal-log')} />
                      <SubmenuItem label="Change Log" onPress={() => onNavigate('change-log')} />
                      <SubmenuItem label="Asset Management" onPress={() => onNavigate('asset-management')} />
                    </View>
                  )}
                </ExpandedNavigationItem>
              </DLOTooltip> */}

          {/* Harbor - delivery entry point */}
          <DLOTooltip
            {...getExpandedTooltipProps('harbor', disabledItems['harbor'])}
            position="right"
            trigger="hover"
            fontSize={14}
          >
            <ExpandedNavigationItem
              id="harbor"
              iconChildren={<HarborIcon />}
              label="Harbor"
              isDisabled={disabledItems['harbor']}
            />
          </DLOTooltip>

          {(
            <>
              {/* Admin Dashboard */}
              <DLOTooltip
                {...getExpandedTooltipProps('admin-dashboard', disabledItems['admin-dashboard'], 'Configurations', 'Administrative controls and configuration settings')}
                position="right"
                trigger="hover"
                fontSize={14}
              >
                <ExpandedNavigationItem
                  id="admin-dashboard"
                  iconChildren={<AdminIcon />}
                  label="Configurations"
                  isDisabled={disabledItems['admin-dashboard']}
                />
              </DLOTooltip>
            </>
          )}

          {/* Ticketing Portal */}
          <DLOTooltip
            title={getTooltipContent('ticketing-portal').title}
            content={getTooltipContent('ticketing-portal').description}
            position="right"
            trigger="hover"
            fontSize={14}
          >
            <ExpandedNavigationItem
              id="ticketing-portal"
              iconChildren={<FolderIcon />}
              label="Ticketing Portal"
              isDisabled={disabledItems['ticketing-portal']}
            />
          </DLOTooltip>
            </View>

            {/* Bottom spacing */}
            <View style={styles.bottomSpacing} />
          </ScrollView>
    );
  };

  // Hide sidebar on mobile devices (below 768px)
  // Show on tablets and desktop for better UX
  if (!shouldShowSidebar) {
    return null;
  }

  // Sidebar container props for web hover tracking
  const sidebarContainerProps = Platform.OS === 'web' ? {
    // @ts-ignore - Web-specific events
    onMouseEnter: () => setIsSidebarHovered(true),
    onMouseLeave: () => setIsSidebarHovered(false),
  } : {};

  return (
    <View 
      ref={sidebarRef}
      style={[
        styles.sidebarContainer,
        isExpanded && { 
          width: isTablet ? 280 : 300,
          alignItems: 'stretch' // Change alignment when expanded
        }
      ]}
      {...sidebarContainerProps}
    >
      {isExpanded ? (
        // Expanded Sidebar Content
        <ExpandedSidebarContent />
      ) : (
        <>
          {/* Menu list - Wrapped in ScrollView for tablet landscape scrolling */}
          <ScrollView 
            style={{ flex: 1 }}
            contentContainerStyle={styles.menuList}
            showsVerticalScrollIndicator={false}
          >
            {/* Menu */}
            <DLOTooltip title="Expand" content="Expand sidebar to see all navigation options" position="right" trigger="hover" fontSize={14}>
              <NavigationItem id="menu" isClickable={true}>
                <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <SideBarMenuIcon />
                </Svg>
              </NavigationItem>
            </DLOTooltip>

            {/* Home */}
            <DLOTooltip 
              content={getTooltipContent('home').title}
              position="right" 
              trigger="hover"
              fontSize={14}
            >
              <NavigationItem id="home" isDisabled={disabledItems['home']}>
                <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <HomeIcon />
                </Svg>
              </NavigationItem>
            </DLOTooltip>

            {(
              <>
                {/* Observations */}
                <DLOTooltip 
                  content={getTooltipContent('observations').title}
                  position="right" 
                  trigger="hover"
                  fontSize={14}
                >
                  <NavigationItem id="observations" isDisabled={disabledItems['observations']}>
                    <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <VisibilityIcon />
                    </Svg>
                  </NavigationItem>
                </DLOTooltip>

                {/* Shift Handover */}
                <DLOTooltip 
                  content={getTooltipContent('shift-handover').title}
                  position="right" 
                  trigger="hover"
                  fontSize={14}
                >
                  <NavigationItem id="shift-handover" isDisabled={disabledItems['shift-handover']}>
                    <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <SwapIcon />
                    </Svg>
                  </NavigationItem>
                </DLOTooltip>

                {/* Leadership Walk */}
                <DLOTooltip 
                  content={getTooltipContent('leadership-walk').title}
                  position="right" 
                  trigger="hover"
                  fontSize={14}
                >
                  <NavigationItem id="leadership-walk" isDisabled={disabledItems['leadership-walk']}>
                    <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <WalkIcon />
                    </Svg>
                  </NavigationItem>
                </DLOTooltip>

                {/* SPARC Walk */}
                <DLOTooltip 
                  content={getTooltipContent('sparc-walk').title}
                  position="right" 
                  trigger="hover"
                  fontSize={14}
                >
                  <NavigationItem id="sparc-walk" isDisabled={disabledItems['sparc-walk']}>
                    <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <ExploreIcon />
                    </Svg>
                  </NavigationItem>
                </DLOTooltip>

                {/* NHO */}
                <DLOTooltip 
                  content={getTooltipContent('nho').title}
                  position="right" 
                  trigger="hover"
                  fontSize={14}
                >
                  <NavigationItem id="nho" isDisabled={disabledItems['nho']}>
                    <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <PersonAddIcon />
                    </Svg>
                  </NavigationItem>
                </DLOTooltip>

                {/* People Hub */}
                <DLOTooltip
                  content={getTooltipContent('people-hub').title}
                  position="right"
                  trigger="hover"
                  fontSize={14}
                >
                  <NavigationItem id="people-hub" isDisabled={disabledItems['people-hub']}>
                    <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <SupervisorIcon />
                    </Svg>
                  </NavigationItem>
                </DLOTooltip>

                {/* Racking Inspection */}
                <DLOTooltip 
                  content={getTooltipContent('racking-inspection').title}
                  position="right" 
                  trigger="hover"
                  fontSize={14}
                >
                  <NavigationItem id="racking-inspection" isDisabled={disabledItems['racking-inspection']}>
                    <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <InventoryIcon />
                    </Svg>
                  </NavigationItem>
                </DLOTooltip>

                {/* Quality Audit */}
                <DLOTooltip 
                  content={getTooltipContent('quality-audit').title}
                  position="right" 
                  trigger="hover"
                  fontSize={14}
                >
                  <NavigationItem id="quality-audit" isDisabled={disabledItems['quality-audit']}>
                    <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <PasteIcon />
                    </Svg>
                  </NavigationItem>
                </DLOTooltip>

                {/* Site Evaluation */}
                <DLOTooltip 
                  content={getTooltipContent('site-evaluation').title}
                  position="right" 
                  trigger="hover"
                  fontSize={14}
                >
                  <NavigationItem id="site-evaluation" isDisabled={disabledItems['site-evaluation']}>
                    <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <VerificationIcon />
                    </Svg>
                  </NavigationItem>
                </DLOTooltip>

                {/* Analytics */}
                {shouldShowAnalyticsHub && (
                  <DLOTooltip
                    content={getTooltipContent('analytics').title}
                    position="right"
                    trigger="hover"
                    fontSize={14}
                  >
                    <NavigationItem id="analytics" isDisabled={disabledItems['analytics']}>
                      <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <AnalyticsIcon />
                      </Svg>
                    </NavigationItem>
                  </DLOTooltip>
                )}

                {/* Action Plan */}
                <DLOTooltip
                  content={getTooltipContent('action-plan').title}
                  position="right"
                  trigger="hover"
                  fontSize={14}
                >
                  <NavigationItem id="action-plan" isDisabled={disabledItems['action-plan']}>
                    <View style={{ paddingTop: 12 }}>
                      <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <ActionPlanIcon />
                      </Svg>
                    </View>
                  </NavigationItem>
                </DLOTooltip>

                {/* Stand Up - Hidden on iOS */}
                {shouldShowStandUp && (
                  <DLOTooltip
                    content={getTooltipContent('stand-up').title}
                    position="right"
                    trigger="hover"
                    fontSize={14}
                  >
                    <NavigationItem id="stand-up" isDisabled={disabledItems['stand-up']}>
                      <Svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <StandUpIcon />
                      </Svg>
                    </NavigationItem>
                  </DLOTooltip>
                )}

                {/* Labor Optimization - Only show on web */}
                {shouldShowLaborOptimization && (
                  <DLOTooltip 
                    content={getTooltipContent('labor-optimization').title}
                    position="right" 
                    trigger="hover"
                    fontSize={14}
                  >
                    <NavigationItem id="labor-optimization" isDisabled={disabledItems['labor-optimization']}>
                      <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <TrendingIcon />
                      </Svg>
                    </NavigationItem>
                  </DLOTooltip>
                )}
              </>
            )}

            {/* Dock Manager - Commented out for later activation */}
            {/* <NavigationItem id="dock-manager">
              <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <FolderIcon />
              </Svg>
            </NavigationItem> */}

            {/* Harbor - delivery entry point */}
            <DLOTooltip
              content={getTooltipContent('harbor').title}
              position="right"
              trigger="hover"
              fontSize={14}
            >
              <NavigationItem id="harbor" isDisabled={disabledItems['harbor']}>
                <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <HarborIcon />
                </Svg>
              </NavigationItem>
            </DLOTooltip>

            {(
              <>
                {/* Admin Dashboard */}
                <DLOTooltip
                  content="Configurations"
                  position="right"
                  trigger="hover"
                  fontSize={14}
                >
                  <NavigationItem id="admin-dashboard" isDisabled={disabledItems['admin-dashboard']}>
                    <Svg width="17" height="17" viewBox="0 0 17 17" fill="none">
                      <AdminIcon />
                    </Svg>
                  </NavigationItem>
                </DLOTooltip>
              </>
            )}

            {/* Ticketing Portal */}
            <DLOTooltip
              content={getTooltipContent('ticketing-portal').title}
              position="right"
              trigger="hover"
              fontSize={14}
            >
              <NavigationItem id="ticketing-portal" isDisabled={disabledItems['ticketing-portal']}>
                <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <FolderIcon />
                </Svg>
              </NavigationItem>
            </DLOTooltip>
          </ScrollView>

          {/* Sticky Bar with PepsiCo Logo - Only show in collapsed state */}
          {/* <View style={styles.stickyBar}>
            <Pressable style={styles.floatingButton}>
              <PepsiCoStickyLogo />
            </Pressable>
          </View> */}
        </>
      )}
    </View>
  );
}

const createSidebarStyles = (isDark: boolean) => StyleSheet.create({
  hoveredNavItem: {
    backgroundColor: isDark ? 'rgba(255, 255, 255, 0.08)' : '#EFF5FC',
  },
  hoveredExpandedNavItem: {
    backgroundColor: isDark ? 'rgba(255, 255, 255, 0.08)' : '#EFF5FC',
  },
  disabledNavItem: {
    opacity: 0.4,
  },
  disabledExpandedNavItem: {
    opacity: 0.4,
  },
  sidebarContainer: {
    width: 72,
    height: 'auto',
    alignItems: 'stretch',
    backgroundColor: isDark ? '#202020' : '#FBFCFE',
    borderRightWidth: 1,
    borderRightColor: isDark ? '#363636' : 'transparent',
    flexDirection: 'column',
    justifyContent: 'space-between',
    paddingVertical: 0,
    flexShrink: 0, // Prevent the sidebar from shrinking
    ...Platform.select({
      ios: {
        shadowColor: isDark ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 92, 188, 0.08)',
        shadowOffset: { width: 4, height: 0 },
        shadowOpacity: 1,
        shadowRadius: 24,
      },
      android: {
        elevation: 8,
      },
    }),
    zIndex: 10,
  },
  expandedSidebarContainer: {
    width: 300,
    height: '100%',
    backgroundColor: isDark ? '#202020' : '#FBFCFE',
    borderRightWidth: 1,
    borderRightColor: isDark ? '#363636' : 'transparent',
    position: 'absolute',
    left: 72, // Width of the collapsed sidebar
    top: 0,
    zIndex: 100,
    ...Platform.select({
      ios: {
        shadowColor: isDark ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 92, 188, 0.08)',
        shadowOffset: { width: 4, height: 0 },
        shadowOpacity: 1,
        shadowRadius: 24,
      },
      android: {
        elevation: 8,
      },
    }),
  },
  scrollContent: {
    flexGrow: 1,
    justifyContent: 'space-between',
    //paddingBottom: 24,
    width: '100%',
  },
  expandedMenuList: {
    //paddingTop: 24,
    gap: 4,
    width: '100%',
    flex: 1,
  },
  menuList: {
    flexGrow: 1,
    //paddingTop: 16,
    //paddingBottom: 16,
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
    width: '100%',
  },
  navItem: {
    height: 56,
    paddingHorizontal: 24,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: isDark ? '#202020' : '#FBFCFE',
    width: '100%',
  },
  activeNavItem: {
    borderLeftWidth: 5,
    borderLeftColor: isDark ? '#3D8BFF' : '#005CBC',
    backgroundColor: isDark ? 'rgba(61, 139, 255, 0.12)' : '#FBFCFE',
  },
  expandedNavItem: {
    height: 56,
    paddingHorizontal: 24,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: isDark ? '#202020' : '#FBFCFE',
    width: '100%',
  },
  activeExpandedNavItem: {
    borderLeftWidth: 5,
    borderLeftColor: isDark ? '#3D8BFF' : '#005CBC',
    backgroundColor: isDark ? 'rgba(61, 139, 255, 0.12)' : '#FBFCFE',
  },
  expandedNavItemContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flex: 1,
  },
  expandedNavItemLabel: {
    flex: 1,
    color: isDark ? '#B0B0B0' : '#616161',
    fontFamily: Platform.OS === 'ios' ? 'System' : '__Inter_d65c78, sans-serif',
    fontSize: 14.5,
    fontWeight: '400',
    lineHeight: 20,
  },
  activeExpandedNavItemLabel: {
    color: isDark ? '#3D8BFF' : '#005CBC',
  },
  submenuContainer: {
    backgroundColor: isDark ? '#1A1A1A' : '#EFF5FC',
    width: '100%',
  },
  submenuItem: {
    paddingVertical: 8,
    paddingHorizontal: 12,
    width: '100%',
  },
  submenuItemContent: {
    paddingLeft: 40,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  submenuItemLabel: {
    flex: 1,
    color: isDark ? '#B0B0B0' : '#616161',
    fontFamily: Platform.OS === 'ios' ? 'System' : '__Inter_d65c78, sans-serif',
    fontSize: 14.5,
    fontWeight: '400',
    lineHeight: 20,
  },
  bottomSpacing: {
    height: 32,
  },
  stickyBar: {
    width: 72,
    paddingHorizontal: 12,
    paddingVertical: 16,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: isDark ? '#202020' : '#FBFCFE',
    flexShrink: 0,
  },
  floatingButton: {
    width: 40,
    height: 40,
    position: 'relative',
    ...Platform.select({
      ios: {
        shadowColor: isDark ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 92, 188, 0.08)',
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 1,
        shadowRadius: 24,
      },
      android: {
        elevation: 4,
      },
    }),
  },
  disabledTooltipContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    maxWidth: 240,
  },
  disabledTooltipIconWrapper: {
    width: 16,
    height: 16,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  disabledTooltipText: {
    flex: 1,
    color: '#E5E5E5',
    fontFamily: Platform.select({
      ios: 'Inter-Regular',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 20,
  },
});
